import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC } from 'react'
import { SlugifiedAttributesFilterItemWithValues } from 'services/providers/bapi/entities/filters/normalize'
import { useAttributesFilterProps } from 'sharedComponents/ProductStream/filters/useAttributesFilterProps'
import { useDisplaySizeFilter } from 'sharedComponents/ProductStream/helpers'
import { useFeatureFlags } from 'store/context'
import styled from 'styled-components'
import Chip from 'uiComponents/atoms/Chip/Chip'
import { bordersColor, darkGrayColor } from 'uiComponents/constants/colors'
import {
  fsXS,
  fsXXS,
  fwBold,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import { useDropdownOptions } from 'uiComponents/organisms/Filters/SimpleDropdownFilter'
import {
  SizeRunFilterType,
  getSizeRunFilterTranslations,
} from './translateFilters'

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const SizeChip = styled(Chip)`
  font-size: ${fsXXS}px;
  ${fwMediumBold};
  margin: 0 10px 10px 0;
  padding: 0 12px;
`

const FilterContainer = styled.div`
  padding: 20px 0;
  margin: 0 24px;
  &:not(:last-child) {
    border-bottom: 1px solid ${bordersColor};
  }
`

const FilterLabel = styled.span`
  display: inline-block;
  padding-bottom: 10px;
  color: ${darkGrayColor};
  font-size: ${fsXS}px;
  ${fwBold};
`

const SizeFilter: FC<{ filter: SlugifiedAttributesFilterItemWithValues }> = ({
  filter,
}) => {
  const { t } = useTranslation()
  const { options, selectedValues, toggleSelection } =
    useAttributesFilterProps(filter)
  const { dropdownOptions, selection, onVariantSelected } = useDropdownOptions({
    options,
    selectedValues,
    toggleSelection,
  })

  const shouldDisplaySizeFilter = useDisplaySizeFilter()
  const { isActive: isFeatureActive } = useFeatureFlags()
  const showFilterNameTranslationFromSlug = isFeatureActive(
    'enableShopSizeRunFilters',
  )

  if (!shouldDisplaySizeFilter) {
    return null
  }

  const translatedFilterName = getSizeRunFilterTranslations({
    sizeRunType: filter.slug as SizeRunFilterType,
    t,
  })

  const filterName =
    showFilterNameTranslationFromSlug && translatedFilterName
      ? translatedFilterName
      : filter.name

  return (
    <FilterContainer>
      {filterName && (
        <FilterLabel data-testid={`FilterLabel_${filterName}`}>
          {filterName}
        </FilterLabel>
      )}
      <ChipContainer data-testid="SizeChipFilterContainer">
        {dropdownOptions[0].options.map(option => {
          const dataTestId = selection.includes(option)
            ? `${option.label}_selected`
            : `${option.label}_unselected`

          return (
            <SizeChip
              key={option.id}
              rectangular={true}
              onClick={() => onVariantSelected(option)}
              active={selection.includes(option)}
              dataTestId={dataTestId}
            >
              {option.label}
            </SizeChip>
          )
        })}
      </ChipContainer>
    </FilterContainer>
  )
}

export default SizeFilter
